import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useAuth } from "../components/AuthProvider";
import { userData } from "../recoil/atom/userData";
import { API_DOMAIN } from "../utils/constants";
import {
  calculateBusinessWiseNPS,
  calculateMonthWiseConsumedMessages,
  calculateMtdNPSScore,
  calculateNPSScore,
  calculateResponseRatio,
  countLowRatingReason,
  countSubConcern,
  getDateCount,
  getNpsScoreForWalkIn,
  getReferralData,
  getSalesEnquiryMap,
} from "../utils/feedbackDataUtils";
import { enquiry, sales } from "../constants/constants";
import { impersonateData } from "../recoil/atom/impersonate";

export const useGetNPSData = () => {
  // Hooks
  const user = useRecoilValue(userData);
  const impersonate = useRecoilValue(impersonateData);
  const actualUser = impersonate?.impersonate ? impersonate.businessDetails : user;

  const { logout } = useAuth();
  const navigate = useNavigate();
  const params = useParams();

  // Feedback Data
  const [npsDetails, setNpsDetails] = useState();
  const [npsData, setNpsData] = useState([]); // This is data fetched for active dashboard
  const [noFeedback, setNoFeedback] = useState([]);
  const [failed, setFailed] = useState([]);
  const [enquiryData, setEnquiryData] = useState([]); // This is data fetched for sales dashboard
  const [salesData, setSalesData] = useState([]); // This is data fetched for enquiry dashboard
  const [activeSlug, setActiveSlug] = useState();
  const [referralData, setReferralData] = useState({
    callBacks: [],
    contacts: [],
  });

  // Loader
  const [loading, setLoading] = useState(true);

  // Dashboard Data
  const [npsScore, setNpsScore] = useState(0);
  const [mtdNpsScore, setMtdNpsScore] = useState({
    totalData: 0,
    npsScore: 0,
    responseRatio: 0,
  });
  const [walkInNpsScore, setWalkInNpsScore] = useState({
    totalData: 0,
    npsScore: 0,
    responseRatio: 0,
  });

  const [concernData, setConcernData] = useState(null);
  const [subConcernData, setSubConcernData] = useState(null);
  const [businessChartData, setBusinessChartData] = useState(null);
  const [monthWiseConsumedMessages, setMonthWiseConsumedMessages] = useState(0);
  const [responseRatio, setResponseRatio] = useState("");
  const [dateCount, setDateCount] = useState([]);
  const [enquirySalesMap, setEnquirySalesMap] = useState({
    retailed: 0,
    notRetailed: 0,
  });
  const [turnaroundTime, setTurnaroundTime] = useState({
    monthly: "",
    overall: "",
  });
  useEffect(() => {
    setActiveSlug(params.feedbackType);
    fetchNPSDetails(params.feedbackType);
  }, [params.feedbackType]);

  useEffect(() => {
    if (!npsDetails) return;
    const npsScore = calculateNPSScore(npsData);
    setNpsScore(npsScore);
    const concernData = countLowRatingReason(npsData);
    setConcernData(concernData);
    const subConcernData = countSubConcern(npsData);
    setSubConcernData(subConcernData);
    const chartData = calculateBusinessWiseNPS(npsData);
    setBusinessChartData(chartData);
    const monthlyCounterData = calculateMonthWiseConsumedMessages(
      npsData,
      noFeedback,
      actualUser?.subscriptionStartDate
    );
    setMonthWiseConsumedMessages(monthlyCounterData);
    const responseRation = calculateResponseRatio(npsData, noFeedback);
    setResponseRatio(responseRation);
    const dateCount = getDateCount(npsDetails);
    setDateCount(dateCount);

    const referralData = getReferralData(npsData);
    setReferralData(referralData);

    const monthData = calculateMtdNPSScore(npsData, noFeedback, actualUser?.subscriptionStartDate);
    setMtdNpsScore(monthData);

    const walkInData = getNpsScoreForWalkIn(npsData, noFeedback);
    setWalkInNpsScore(walkInData);
    setLoading(false);

    if (activeSlug === sales.toLowerCase()) {
      const enquirySalesMap = getSalesEnquiryMap(npsData, enquiryData);
      setEnquirySalesMap(enquirySalesMap);
    }
    getTurnaroundTimeOverallAndMonthly(npsData);
  }, [npsDetails]);


  const fetchNPSDetails = async (feedbackType) => {
    try {
      const isImpersonating =
        sessionStorage.getItem("impersonate") === "true" &&
        sessionStorage.getItem("business");

      const endpointContext = isImpersonating ? `impersonate` : "nps-details";

      const token = isImpersonating
        ? sessionStorage.getItem("token")
        : localStorage.getItem("token");
      const response = await axios.get(`${API_DOMAIN}/${endpointContext}`, {
        params: isImpersonating
          ? {
            businessId: sessionStorage.getItem("business"),
            feedbackType: feedbackType.toLowerCase(),
          }
          : { feedbackType: feedbackType.toLowerCase() },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setNpsDetails(response.data);
      setNpsData(response.data?.feedback ?? []);
      setNoFeedback(response.data?.noFeedback ?? []);
      setFailed(response.data?.failed ?? []);
      setEnquiryData(response.data?.enquiryDataForSales ?? []);
      setSalesData(response.data?.salesDataForEnquiry ?? []);
    } catch (err) {
      console.error(err);
      logout(navigate("/"));
    }
  };

  const filterDataForDate = (filterDate) => {
    if (filterDate?.startDate === null && filterDate?.endDate === null) {
      setNpsData(npsDetails?.feedback ?? []);
      setNoFeedback(npsDetails?.noFeedback ?? []);
      setFailed(npsDetails?.failed ?? []);
    } else {
      let filteredFeedbackData = npsDetails?.feedback?.filter((feedback) => {
        let date = new Date(feedback["created"]);
        return date >= filterDate.startDate && date <= filterDate.endDate;
      });

      let filteredNoFeedbackData = npsDetails?.noFeedback?.filter(
        (feedback) => {
          let date = new Date(feedback["created"]);
          return date >= filterDate.startDate && date <= filterDate.endDate;
        }
      );

      let filteredFailedData = npsDetails?.failed?.filter((feedback) => {
        let date = new Date(feedback["updated"]);
        return date >= filterDate.startDate && date <= filterDate.endDate;
      });

      setNpsData(filteredFeedbackData ?? []);
      setNoFeedback(filteredNoFeedbackData ?? []);
      setFailed(filteredFailedData ?? []);
    }
  };

  const switchDashboard = () => {
    setLoading(true);
    if (activeSlug === enquiry.toLowerCase()) {
      navigate(`/dashboard/${sales.toLowerCase()}`);
      setActiveSlug(sales.toLowerCase());
    } else {
      navigate(`/dashboard/${enquiry.toLowerCase()}`);
      setActiveSlug(enquiry.toLowerCase());
    }
  };

  const getTurnaroundTimeOverallAndMonthly = (npsData) => {
    const overallTurnaroundTime = turnAroundTimeCalculation(npsData);

    const monthWiseFeedback = turnAroundTimeCalculation(
      monthWiseFeedbackData(npsData)
    );
    setTurnaroundTime({
      monthly: monthWiseFeedback,
      overall: overallTurnaroundTime,
    });
  };
  const monthWiseFeedbackData = () =>
    npsData?.filter((item) => {
      const month = new Date()?.getMonth();

      const updated = new Date(item?.updated)?.getMonth();
      return month === updated;
    });
  const turnAroundTimeCalculation = (data) => {
    const MS_PER_DAY = 86_400_000;
    const toUTC = (raw) => {
      if (!raw) return null;

      if (typeof raw === "string") {
        if (raw.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
          return new Date(`${raw}T00:00:00Z`);
        }

        // Match DD/MM/YYYY hh:mm:ss AM/PM
        const match = raw.match(
          /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2}) (AM|PM)$/i
        );
        if (match) {
          let [, dd, mm, yyyy, hh, min, sec, ampm] = match;
          let hours = parseInt(hh, 10);
          if (ampm.toUpperCase() === "PM" && hours !== 12) hours += 12;
          if (ampm.toUpperCase() === "AM" && hours === 12) hours = 0;

          return new Date(
            Date.UTC(
              parseInt(yyyy),
              parseInt(mm) - 1,
              parseInt(dd),
              hours,
              parseInt(min),
              parseInt(sec)
            )
          );
        }

        // Fallback to native parse
        const iso = new Date(raw);
        if (!isNaN(iso)) return iso;
      }

      // If it's a number or epoch string
      const num = Number(raw);
      if (!isNaN(num)) return new Date(num);

      return null;
    };

    const validDiffs = data
      ?.map(({ created, metadata }) => {
        const invoiceRaw = metadata?.invoiceDate;
        const createdDate = toUTC(created);
        const invoiceDate = toUTC(invoiceRaw);

        if (
          !createdDate ||
          isNaN(createdDate) ||
          !invoiceDate ||
          isNaN(invoiceDate)
        ) {
          return null;
        }

        return Math.round((createdDate - invoiceDate) / MS_PER_DAY);
      })
      .filter((diff) => diff !== null);

    return (
      (validDiffs?.reduce((sum, d) => sum + d, 0) / validDiffs?.length).toFixed(
        1
      ) || 0
    );
  };

  return {
    fetchNPSDetails,
    npsDetails,
    filterDataForDate,
    npsData,
    noFeedback,
    failed,
    loading,
    user: impersonate?.impersonate ? impersonate.businessDetails : user,
    npsScore,
    concernData,
    subConcernData,
    businessChartData,
    monthWiseConsumedMessages,
    responseRatio,
    dateCount,
    activeSlug,
    switchDashboard,
    referralData,
    mtdNpsScore,
    enquirySalesMap,
    salesData,
    enquiryData,
    turnaroundTime,
    walkInNpsScore
  };
};
